import { yupResolver } from '@hookform/resolvers/yup';
import ContentPasteGoOutlinedIcon from '@mui/icons-material/ContentPasteGoOutlined';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { handlePasteToForm, isObjectEmpty } from 'src/helpers';
import { setNotice } from 'src/redux/noticesSlice';
import {
  IKloudParams,
  IProvider,
  useUpdateAvatarMutation,
  useUpdateProviderMutation,
} from 'src/redux/services/provider';
import * as yup from 'yup';
import { CustomTypography } from '../../custom/CustomTypography';
import { PasswordTextfield } from '../../custom/PasswordTextfield';
import { ConfirmationModal } from '../../modal/ConfirmationModal';
import { SharedFormProvider } from '../../shared/FormProvider';
import { SidePanel } from '../../sidePanel/SidePanel';
import { ProviderHeader } from '../ProviderHeader';
import { IEditProviderProps } from './EditProvider';
import styles from './EditProvider.module.css';
import { ENoticeType } from 'src/enums';

const schema = yup.object({
  name: yup
    .string()
    .required('Обязательное поле')
    .max(50, 'Название не может превышать 50 символов')
    .matches(/[^ ]+/, {
      message: 'Название не может состоять только из пробелов',
    })
    .matches(/^[^ ]+( *[^ ]+)*?$/, 'Название не может содержать пробелы в начале и конце'),
  description: yup
    .string()
    .max(255, 'Описание не может превышать 255 символов')
    .matches(/^$|[^ ]+/, {
      message: 'Описание не может состоять только из пробелов',
    }),
  is_public: yup.boolean(),
  password_required: yup.boolean(),
  params: yup.object({
    issuer: yup
      .string()
      .url('Неверный формат ссылки')
      .min(1, 'Обязательное поле')
      .max(2000, 'Адрес не может превышать 2000 символов')
      .nullable(true)
      .transform((v) => (typeof v === 'undefined' ? null : v)),
    external_client_id: yup
      .string()
      .max(255, 'Идентификатор не может превышать 255 символов')
      .required('Обязательное поле')
      .matches(/^[^\n ]*$/, {
        message: 'Идентификатор не может содержать пробелы',
      }),
    external_client_secret: yup
      .string()
      .max(255, 'Секретный ключ не может превышать 255 символов')
      .required('Обязательное поле')
      .matches(/^[^\n ]*$/, {
        message: 'Секретный ключ не может содержать пробелы',
      }),
  }),
});

export const EditKloudProvider: FC<IEditProviderProps> = ({ isOpen, onClose, provider }) => {
  const dispatch = useDispatch();
  const methods = useForm<IProvider<IKloudParams>>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const {
    register,
    handleSubmit,
    formState: { dirtyFields, errors },
    clearErrors,
    reset,
    control,
  } = methods;

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [updateProvider, updateResult] = useUpdateProviderMutation();
  const [updateAvatar] = useUpdateAvatarMutation();

  const uploadedAvatar = useWatch({ control, name: 'avatar' });

  useEffect(() => {
    if (updateResult.isSuccess) onClose();
  }, [updateResult]);

  useEffect(() => {
    if (provider && isOpen) {
      reset(provider as IProvider<IKloudParams>);
    }
  }, [isOpen]);

  const closeSaveModal = () => setSaveModalOpen(false);

  const handleClose = () => {
    if (isObjectEmpty(dirtyFields)) onClose();
    else setSaveModalOpen(true);
  };

  const pasteFields = async (provider?: Partial<IProvider<IKloudParams>>) => {
    try {
      await handlePasteToForm(methods, provider);
    } catch (err) {
      dispatch(
        setNotice({
          type: ENoticeType.error,
          id: Math.random(),
          isRead: false,
          message: `Ошибка вставки параметров: ${err}`,
          timestamp: new Date().toString(),
        }),
      );
    }
  };

  const onSubmit: SubmitHandler<IProvider<IKloudParams>> = (data) => {
    updateProvider(data).then(() => {
      setTimeout(() => {
        updateAvatar({
          clientId: data.client_id,
          providerId: data.id,
          avatar: uploadedAvatar,
        });
      }, 1000);
    });
  };

  return (
    <SidePanel
      onClose={handleClose}
      isOpen={isOpen}
      title="Редактировать способ входа KLOUD"
      AdditionalAction={() => pasteFields()}
      customAdditionalText="Вставить настройки"
      CustomAdditionalIcon={ContentPasteGoOutlinedIcon}
      isNoBackdrop
    >
      <SharedFormProvider<IProvider<IKloudParams>>
        methods={methods}
        onSubmit={handleSubmit(onSubmit)}
        handleCancel={handleClose}
        isDisabled={updateResult.isLoading || isObjectEmpty(dirtyFields)}
      >
        <div className={styles['create-provider-form']}>
          <ProviderHeader />
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Идентификатор ресурса (client_id)
          </CustomTypography>
          <TextField
            {...register('params.external_client_id', {
              required: true,
              onChange: () => {
                if (errors.params?.external_client_id) clearErrors('params.external_client_id');
              },
            })}
            className="custom"
            error={!!errors.params?.external_client_id}
            helperText={errors.params?.external_client_id?.message}
            fullWidth
            variant="standard"
            autoComplete="off"
          />
          <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
            Уникальный идентификатор подключаемого ресурса
          </CustomTypography>
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Секретный ключ (client_secret)
          </CustomTypography>
          <PasswordTextfield
            nameField="params.external_client_secret"
          />
          <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
            Секретный ключ ресурса
          </CustomTypography>
          <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
            Базовый адрес сервера авторизации (issuer)
          </CustomTypography>
          <TextField
            {...register('params.issuer', {
              required: true,
              onChange: () => {
                if (errors.params?.issuer) clearErrors('params.issuer');
              },
            })}
            style={{ marginBottom: 32 }}
            className="custom"
            error={!!errors.params?.issuer}
            helperText={errors.params?.issuer?.message}
            fullWidth
            variant="standard"
          />
        </div>
      </SharedFormProvider>

      <ConfirmationModal
        title="Сохранение изменений"
        mainMessage={['Изменения не сохранены. Продолжить без сохранения?']}
        actionButtonText="Продолжить"
        isOpen={saveModalOpen}
        onAction={() => {
          onClose();
          setSaveModalOpen(false);
        }}
        onClose={closeSaveModal}
      />
    </SidePanel>
  );
};
